/**
 * DatePicker component module.
 * @module shared/components/DatePicker
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import debug from 'debug';
import styled, {withTheme} from 'styled-components/macro';
import {usePopper} from 'react-popper';
import {fill} from './cssUtils';
import Icon from './Icon';
import TextInput from './TextInput';
import {Calendar, utils} from '@hassanmojab/react-modern-calendar-datepicker';
import '@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css';
import './DatePicker.css';
import {getHexWithOpacity} from '../utils/colors';

const log = debug('circleblack:components:datepicker');

// These constants further restrict the allowable year range.
// They are internal to the "react-modern-calendar-datepicker" component and can not be configured.
// So they act in conjunction with the {minDate} and {maxDate} props.
const MINIMUM_SELECTABLE_YEAR_SUBTRACT = 100;
const MAXIMUM_SELECTABLE_YEAR_SUM = 50;

// Basic, reusable string formats
const DATE_VALUE_FORMAT = 'YYYY-MM-DD';
const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';

// Static popper options
const POPPER_OPTIONS = {
  placement: 'bottom-start',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [5, 0],
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-start', 'left', 'right'],
      },
    },
  ],
};

function convertStringToDay(dateString) {
  if (!dateString) {
    return null;
  }

  const asMoment = moment(dateString, DATE_VALUE_FORMAT, true);
  if (!asMoment.isValid()) {
    return null;
  }

  return {
    year: asMoment.year(),
    month: asMoment.month() + 1,
    day: asMoment.date(),
  };
}

function convertDayToValue(day) {
  return moment({
    year: day.year,
    month: day.month - 1,
    date: day.day,
  }).format(DATE_VALUE_FORMAT);
}

function convertDayToDisplay(day) {
  if (!day) {
    return '';
  }

  const asMoment = moment({
    year: day.year,
    month: day.month - 1,
    date: day.day,
  });

  if (!asMoment.isValid()) {
    return '';
  }

  return asMoment.format(DATE_DISPLAY_FORMAT);
}

function convertDisplayToValue(displayString) {
  if (!displayString) {
    return null;
  }

  const asMoment = moment(displayString, DATE_DISPLAY_FORMAT, true);
  if (!asMoment.isValid()) {
    return null;
  }

  return asMoment.format(DATE_VALUE_FORMAT);
}

function getYearBounds(value, minDate, maxDate) {
  const today = utils().getToday();
  const valueDay = convertStringToDay(value);
  const minDay = convertStringToDay(minDate);
  const maxDay = convertStringToDay(maxDate);

  let min;
  let max;

  if (minDay && valueDay) {
    min = minDay.year < valueDay.year ? minDay.year : valueDay.year;
  } else if (minDay) {
    min = minDay.year;
  } else {
    min = today.year - MINIMUM_SELECTABLE_YEAR_SUBTRACT;
  }

  if (maxDay && valueDay) {
    max = maxDay.year > valueDay.year ? maxDay.year : valueDay.year;
  } else if (maxDay) {
    max = maxDay.year;
  } else {
    max = today.year + MAXIMUM_SELECTABLE_YEAR_SUM;
  }

  return {
    min,
    max,
  };
}

function isWithinRange(value, minDate, maxDate) {
  const asMoment = moment(value, DATE_VALUE_FORMAT, true);
  const yearBounds = getYearBounds(value, minDate, maxDate);

  if (yearBounds.min > asMoment.year()) {
    return false;
  }

  if (yearBounds.max < asMoment.year()) {
    return false;
  }

  if (minDate && moment(minDate, DATE_VALUE_FORMAT, true) > asMoment) {
    return false;
  }

  if (maxDate && moment(maxDate, DATE_VALUE_FORMAT, true) < asMoment) {
    return false;
  }

  return true;
}

function replaceIconsEffect() {
  let styleEl = document.getElementById('cb-datepicker-style');
  if (styleEl) {
    return;
  }

  styleEl = document.createElement('style');
  styleEl.id = 'cb-datepicker-style';

  // used this approach to prevent React from inserting <br/> elements
  styleEl.innerText = '.cb-datepicker-calendar .Calendar__monthArrowWrapper.-left span {background-image: url("/icons/pagination_left_icon.svg")!important;} .cb-datepicker-calendar .Calendar__monthArrowWrapper.-right span {background-image: url("/icons/pagination_right_icon.svg")!important;}';

  document.body.appendChild(styleEl);
  return () => {
    document.body.removeChild(styleEl);
  };
}

const _DatePickerWrapper = styled.div`
  position: relative;
  ${props => fill(props.width)};
  ${props => props.height ? `height: ${props.height}` : ''};
  ${props => props.layout?.grow ? `flex-grow: ${props.layout.grow};` : ''}
  margin-right: 5px;
`;

const _IconWrapper = styled.div`
  position: absolute;
  right: 7px;
  height: 20px;
  top: calc(50% - ${props => props.title ? 5 : 10}px);
`;

const _CalendarWrapper = styled.div`
  & .Calendar {
    ${props => props.theme.neumorphic ? `background-color: ${getHexWithOpacity(props.theme.tileBackgroundColor, props.readOnly ? 0 : 1)}` : ''};
    ${props => props.theme.neumorphic
    ? `background-image: linear-gradient(to bottom right, rgba(255, 255, 255, ${props.readOnly ? 0 : 0.07}), rgba(255, 255, 255, ${props.readOnly ? 0 : 0.07}))`
    : ''};
    ${props => props.theme.neumorphic
    ? `box-shadow: 1px 2px 6px 0 rgba(0, 0, 0, 0.5),
                   inset -1px -1px 0 0 rgba(0, 0, 0, 0.4),
                   inset 1px 1px 0 0 rgba(255, 255, 255, 0.1)` : ''};
  }
  
  & .Calendar__day.-selected {
    ${props => props.theme.neumorphic ? 'box-shadow: 1px 1px 4px 0 rgba(0, 0, 0, 0.5)' : ''};
  }
`;

/**
 * DatePicker component
 * @name DatePicker
 */
const DatePicker = ({
  title,
  layout,
  required,
  help,
  width,
  height,
  placeholder,
  dataPoint,
  readOnly,
  onChange,
  disabled,
  minDate,
  maxDate,
  defaultDate,
  disabledDates,
}) => {
  // convert the date point value to a value usable by the 3rd party calendar
  const value = convertStringToDay(dataPoint?.value ? dataPoint.value : defaultDate);
  const yearBounds = getYearBounds(dataPoint?.value, minDate, maxDate);
  const originalValue = React.useMemo(() => dataPoint?.value, []);

  // hooks/effects
  const [refEl, setRefElement] = useState(null);
  const [popperEl, setPopperElement] = useState(null);
  const [textRef, setTextRef] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [displayValue, setDisplayValue] = useState(null);

  useEffect(() => {
    if (readOnly) setDisplayValue(null);
  }, [readOnly]);

  // validate dataPoint and parsed value
  useEffect(() => {
    if (dataPoint?.value) {
      if (!convertStringToDay(dataPoint.value)) {
        log(`DatePicker: dataPoint is invalid "${dataPoint.value}"`);
        onChange(dataPoint.name, null);
      } else if (!isWithinRange(dataPoint.value, minDate, maxDate)) {
        log(`DatePicker: value is outside allowable range "${dataPoint.value}"`);
      }
    }
  }, [value, dataPoint, onChange]);

  // validate date values that do not change often
  useEffect(() => {
    if (minDate && !convertStringToDay(minDate)) {
      log(`DatePicker: minDate is invalid "${minDate}"`);
    }

    if (maxDate && !convertStringToDay(maxDate)) {
      log(`DatePicker: maxDate is invalid "${maxDate}"`);
    }

    if (minDate && maxDate && minDate > maxDate) {
      log(`DatePicker: minDate "${minDate}" is greater than maxDate "${maxDate}"`);
    }

    disabledDates.map(o => {
      if (o && !convertStringToDay(o)) {
        log(`DatePicker: disabledDates is invalid "${o}"`);
      }
    });
  }, [minDate, maxDate, disabledDates]);

  // create and clean-up dynamic CSS block for calendar icons
  useEffect(replaceIconsEffect);

  // add/removes a handler for hiding the popper when a click occurs outside this component
  useEffect(() => {
    function onAutoHideHandler(e) {
      let currentEl = e.target;
      while (currentEl) {
        if (currentEl === popperEl || currentEl === refEl || currentEl === textRef) {
          return;
        }

        currentEl = currentEl.parentElement;
      }

      setShowCalendar(false);
    }

    function onEscapeHandler(e) {
      if (showCalendar && e.key === 'Escape') {
        setShowCalendar(false);
      }
    }

    document.documentElement.addEventListener('click', onAutoHideHandler);
    document.documentElement.addEventListener('keydown', onEscapeHandler);
    return () => {
      document.documentElement.removeEventListener('click', onAutoHideHandler);
      document.documentElement.removeEventListener('keydown', onEscapeHandler);
    };
  });

  // calculate the positioning of the calendar
  const {styles, attributes} = usePopper(refEl, popperEl, POPPER_OPTIONS);

  // update the state of the calendar if necessary
  if (showCalendar && (disabled || readOnly)) {
    setShowCalendar(false);
  }

  return <React.Fragment>
    <_DatePickerWrapper
      className='cb-datepicker'
      layout={layout}
      width={width}
      height={height}
      ref={setRefElement}>
      <div ref={setTextRef}>
        <TextInput
          type='datepicker'
          autoComplete='off'
          help={help}
          title={title}
          height={height}
          readOnly={readOnly}
          disabled={disabled}
          dependency={showCalendar}
          placeholder={readOnly ? '-' : placeholder}
          validations={{
            custom: target => {
              if (!target.value && required === true) {
                return 'Date is required';
              }
              if (!target.value) {
                return '';
              }

              const asValue = convertDisplayToValue(target.value);
              if (!asValue) {
                return 'Invalid date format';
              }
              if (originalValue !== dataPoint?.value
                && (!isWithinRange(asValue, minDate, maxDate) || disabledDates?.includes(dataPoint?.value))) {
                return `Date is outside allowable range "${asValue}"`;
              }

              return '';
            },
          }}
          dataPoint={{
            name: dataPoint.name,
            value: displayValue === null ? convertDayToDisplay(value) : displayValue,
          }}
          action={() => {
            if (!readOnly && !disabled && displayValue === null) {
              setShowCalendar(!showCalendar);
            } else if (displayValue !== null && !showCalendar) {
              setShowCalendar(true);
            }
          }}
          onChange={(name, display) => {
            // value is changing via keyboard input, attempt to convert the
            // current displayed value to a valid date string format
            let asValue = convertDisplayToValue(display);

            // if we have a valid value, ensure that it is within the allowable range,
            // and clear the value if it is not
            if (!isWithinRange(asValue, minDate, maxDate)) {
              asValue = null;
            }

            // if we have a valid value, we no longer need the display value, clear it
            setDisplayValue(asValue ? null : display);

            //
            onChange(name, asValue);
          }}
        />
      </div>
      {!readOnly && <_IconWrapper title={title}>
        <Icon
          height='20px'
          width='20px'
          margin='0 10px 0 0'
          svg='calendar'
          allowEventPropagation={true}
          disabled={readOnly || disabled}
          action={() => {
            setShowCalendar(!showCalendar);
          }}
        />
      </_IconWrapper>}
    </_DatePickerWrapper>
    {showCalendar ? (
      <_CalendarWrapper
        className='cb-datepicker-calendar'
        ref={setPopperElement}
        style={{...styles.popper, zIndex: 999}}
        {...attributes.popper}>
        <Calendar
          value={value}
          minimumDate={convertStringToDay(minDate)}
          maximumDate={convertStringToDay(maxDate)}
          selectorStartingYear={yearBounds.min}
          selectorEndingYear={yearBounds.max}
          disabledDays={disabledDates.map(convertStringToDay)}
          onChange={(newValue) => {
            if (showCalendar) {
              setDisplayValue(null);
            }

            onChange(dataPoint.name, convertDayToValue(newValue));

            // this is delayed because, we need to trigger validation via the {dependency} prop in the TextInput component
            // if it is done synchronously, validation occurs against the wrong value
            setTimeout(() => setShowCalendar(false), 100);
          }}
        />
      </_CalendarWrapper>
    ) : null}
  </React.Fragment>;
};

export default withTheme(DatePicker);

/**
 *
 */
DatePicker.propTypes = {
  /**
   *
   */
  disabled: PropTypes.bool,

  /**
   *
   */
  readOnly: PropTypes.bool,

  /**
   *
   */
  height: PropTypes.any,

  /**
   *
   */
  width: PropTypes.any,

  /**
   *
   */
  title: PropTypes.string.isRequired,

  /**
   *
   */
  placeholder: PropTypes.string,

  /**
   *
   */
  layout: PropTypes.object,

  /**
   *
   */
  theme: PropTypes.object,

  /**
   * dataPoint represents the date value being passed to the component
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
  }),

  /**
   *
   */
  help: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.node.isRequired,
  }),

  /**
   * defaultDate represents the initial value to be used assuming {dataPoint.value} is not provided
   */
  defaultDate: PropTypes.string,

  /**
   *
   */
  minDate: PropTypes.string.isRequired,

  /**
   *
   */
  maxDate: PropTypes.string.isRequired,

  /**
   * validDates is an optional array of Day objects.
   */
  disabledDates: PropTypes.arrayOf(PropTypes.string),

  /**
   *
   */
  onChange: PropTypes.func,

  required: PropTypes.bool,
};

DatePicker.defaultProps = {
  defaultDate: null,
  disabledDates: [],
  onChange: () => { },
};

